import type { SqliteAsyncConnection } from '../../shared/localDb';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { resolveModuleOnlineHonestyActive } from '../../shell/feature-flags/moduleOnlineHonesty';
import {
  OfflineWriteBlockedError,
  assertWebOnlineWriteAllowed,
  resolveWebOnlineWritesActive,
} from '../../shell/feature-flags/webOnlineWrites';
import { dispatchOnlineCommand } from './commandDispatch';
import { ensureGenericCommandPort, getCommandPort } from './commandPorts';
import {
  ingestOutcomeSucceeded,
  ingestSingleCommand,
  isSaleInboxEntity,
  shouldUseInboxIngest,
  type IngestCommandOutcome,
} from './ingestDeviceCommands';
import { isOnlineOnlyEntity } from './moduleTransportPolicy';
import { toOnlineCommandItem } from './onlineCommandItem';
import type { SyncEntity, SyncOperation } from './syncTypes';

export type SubmitCommandParams = {
  entity: SyncEntity;
  operation: SyncOperation;
  payload: Record<string, unknown>;
  connection?: SqliteAsyncConnection;
};

/** Resultado del tubo: callers de caja web usan applied / already_open sin mirar solo sync_queue. */
export type SubmitCommandResult = {
  applied: boolean;
  enqueued: boolean;
  ingestOutcome?: IngestCommandOutcome;
  ingestError?: string | null;
};

/**
 * El server rechazó el comando (validación de negocio, no fallo técnico).
 * Subclase de Error con el mismo `message` que antes — los callers existentes
 * no notan el cambio; los que quieren el resultado estructurado (cash_session)
 * lo capturan y lo convierten en SubmitCommandResult.
 */
export class IngestRejectedError extends Error {
  readonly ingestError: string;
  constructor(ingestError: string) {
    super(ingestError);
    this.name = 'IngestRejectedError';
    this.ingestError = ingestError;
  }
}

function branchIdFromPayload(payload: Record<string, unknown>): string | null {
  const branchId = payload.branchId;
  return typeof branchId === 'string' && branchId.trim().length > 0 ? branchId.trim() : null;
}

/**
 * Tubo online: gate web offline → ingest (hot set) / RPC.
 * ADR-017 Fase 4: ram-only — sin cola local. Failed ingest → return failed / throw.
 * S2: sale/sale_void no usan este tubo (process_sale_command / void remoto).
 */
export async function submitCommand(params: SubmitCommandParams): Promise<SubmitCommandResult> {
  const { entity, operation, payload } = params;

  await assertWebOnlineWriteAllowed();

  if (isSaleInboxEntity(entity)) {
    return { applied: false, enqueued: false, ingestError: 'web_no_local_queue' };
  }

  ensureGenericCommandPort(entity);
  const port = getCommandPort(entity);
  if (!port) {
    throw new Error(`submitCommand: sin puerto registrado para entity ${entity}`);
  }

  const online = await isOnline().catch(() => false);
  const honesty = await resolveModuleOnlineHonestyActive();

  if (online && (await shouldUseInboxIngest(entity))) {
    const prepared = port.prepareOnlinePayload(operation, payload);
    const ingestResult = await ingestSingleCommand(
      entity,
      operation,
      prepared,
      branchIdFromPayload(prepared),
    );

    if (ingestResult.error === 'inbox_disabled') {
      return {
        applied: false,
        enqueued: false,
        ingestOutcome: ingestResult.outcome,
        ingestError: ingestResult.error ?? 'inbox_disabled',
      };
    } else if (ingestOutcomeSucceeded(ingestResult.outcome)) {
      const item = toOnlineCommandItem(entity, operation, prepared);
      await port.afterServerApply?.(item);
      return {
        applied: true,
        enqueued: false,
        ingestOutcome: ingestResult.outcome,
        ingestError: ingestResult.error ?? null,
      };
    } else if (ingestResult.outcome === 'rejected') {
      throw new IngestRejectedError(ingestResult.error ?? 'ingest_rejected');
    } else {
      // failed — sin cola local, retornar failed
      return {
        applied: false,
        enqueued: false,
        ingestOutcome: ingestResult.outcome,
        ingestError: ingestResult.error ?? null,
      };
    }
  }

  if (online && (await resolveWebOnlineWritesActive())) {
    await dispatchOnlineCommand({ entity, operation, payload });
    return { applied: true, enqueued: false };
  }

  if (honesty && isOnlineOnlyEntity(entity, payload)) {
    if (!online) {
      throw new OfflineWriteBlockedError();
    }
    await dispatchOnlineCommand({ entity, operation, payload });
    return { applied: true, enqueued: false };
  }

  throw new OfflineWriteBlockedError();
}
